import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface DnsRecord {
  type: string;
  name: string;
  value: string;
  purpose: string;
}

interface DomainVerification {
  id: string;
  domain: string;
  owner_type: string;
  status: 'pending' | 'verified' | 'failed' | 'expired';
  verification_token: string;
  verified_at: string | null;
  last_check_at: string | null;
  check_count: number;
  failure_reason: string | null;
  ssl_status: string;
  created_at: string;
  expires_at: string;
  is_expired: boolean;
  days_remaining: number | null;
  dns_records: DnsRecord[];
}

interface InitiateResponse {
  verification_id: string;
  domain: string;
  expires_at: string;
  dns_records: DnsRecord[];
}

interface CheckResponse {
  status: string;
  domain: string;
  verified_at?: string;
  message?: string;
  check_count?: number;
  failure_reason?: string;
}

export function useDomainVerification() {
  const [verification, setVerification] = useState<DomainVerification | null>(null);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [polling, setPolling] = useState(false);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const { toast } = useToast();

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  const initiateDomain = useCallback(async (domain: string): Promise<InitiateResponse | null> => {
    setLoading(true);
    setError(null);

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) {
        throw new Error('Not authenticated');
      }

      const response = await supabase.functions.invoke('Calie_initiateDomainVerification', {
        body: { domain },
      });

      if (response.error) {
        throw new Error(response.error.message || 'Failed to initiate verification');
      }

      const data = response.data as InitiateResponse;
      
      // Fetch full status
      await getStatus(data.verification_id);
      
      toast({
        title: 'Domain verification initiated',
        description: `Please configure your DNS records for ${domain}`,
      });

      return data;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to initiate verification';
      setError(message);
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
      return null;
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const getStatus = useCallback(async (verificationId: string): Promise<DomainVerification | null> => {
    try {
      const response = await supabase.functions.invoke('Calie_getDomainStatus', {
        body: { verification_id: verificationId },
      });

      if (response.error) {
        throw new Error(response.error.message || 'Failed to get status');
      }

      const data = response.data as DomainVerification;
      setVerification(data);
      return data;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to get status';
      setError(message);
      return null;
    }
  }, []);

  const checkVerification = useCallback(async (verificationId: string): Promise<CheckResponse | null> => {
    setChecking(true);
    setError(null);

    try {
      const response = await supabase.functions.invoke('Calie_checkDomainVerification', {
        body: { verification_id: verificationId },
      });

      if (response.error) {
        throw new Error(response.error.message || 'Failed to check verification');
      }

      const data = response.data as CheckResponse;
      
      // Refresh full status
      await getStatus(verificationId);

      if (data.status === 'verified') {
        toast({
          title: 'Domain verified!',
          description: `${data.domain} has been successfully verified.`,
        });
        stopPolling();
      }

      return data;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to check verification';
      setError(message);
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
      return null;
    } finally {
      setChecking(false);
    }
  }, [getStatus, toast]);

  const startPolling = useCallback((verificationId: string, intervalMs = 10000) => {
    // Clear existing interval
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    setPolling(true);

    // Initial check
    getStatus(verificationId);

    // Set up polling
    pollingIntervalRef.current = setInterval(async () => {
      const status = await getStatus(verificationId);
      if (status && (status.status === 'verified' || status.status === 'expired')) {
        stopPolling();
      }
    }, intervalMs);
  }, [getStatus]);

  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    setPolling(false);
  }, []);

  const reset = useCallback(() => {
    setVerification(null);
    setError(null);
    setLoading(false);
    setChecking(false);
    stopPolling();
  }, [stopPolling]);

  return {
    verification,
    loading,
    checking,
    error,
    polling,
    initiateDomain,
    checkVerification,
    getStatus,
    startPolling,
    stopPolling,
    reset,
  };
}
